import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { AlertCircle, CheckCircle2, HeartHandshake, Loader2 } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';
import { useTipPageData } from '@/hooks/payment/useTipPageData';

type TipPageSearch = {
  status?: 'success' | 'cancel';
};

export const Route = createFileRoute('/(public)/tip/$id')({
  validateSearch: (search: Record<string, unknown>): TipPageSearch => ({
    status: search.status === 'success' || search.status === 'cancel' ? search.status : undefined,
  }),
  component: TipPage,
});

/** Exported for tests: the file route only mounts it. */
export function TipPage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useTipPageData(id);
  // Nothing on this card but a "Choose tip amount" button that a customer who
  // followed a tip link has already chosen — the picker names the business and
  // the amount, so send them there and keep this page for the outcomes.
  const isOutcome = search.status !== undefined || Boolean(data?.alreadyTipped);
  const sendToPicker = Boolean(data) && !isOutcome;
  useEffect(() => {
    if (!sendToPicker) return;
    void navigate({ to: '/tip-option/$id', params: { id }, replace: true });
  }, [id, navigate, sendToPicker]);

  if (isLoading || sendToPicker) {
    return (
      <PublicCenteredPage>
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </PublicCenteredPage>
    );
  }

  if (isError || !data) {
    return (
      <PublicCenteredPage>
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle>Tip page unavailable</CardTitle>
            <CardDescription>
              This tip page is invalid, expired, or the invoice is not eligible for tipping.
            </CardDescription>
          </CardHeader>
        </Card>
      </PublicCenteredPage>
    );
  }

  const returnHref = data.returnUrl ?? `/invoice/${id}`;
  const formatMoney = (cents: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: data.currency.toUpperCase(),
    }).format(cents / 100);
  const formattedTip =
    typeof data.currentTipAmount === 'number' && data.currentTipAmount > 0
      ? formatMoney(data.currentTipAmount)
      : null;

  return (
    <PublicCenteredPage>
      {/* Who and what this is about: the payload carries them, and an
          anonymous "Leave a tip" card gave the customer nothing to check. */}
      <header className="mb-6 text-center">
        {data.companyLogoUrl ? (
          <img
            src={data.companyLogoUrl}
            alt={data.companyName ?? 'Business logo'}
            className="mx-auto mb-2 h-12 object-contain"
            loading="lazy"
          />
        ) : data.companyName ? (
          <h1 className="text-xl font-bold text-foreground">{data.companyName}</h1>
        ) : null}
      </header>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-pink-50">
            {search.status === 'success' || data.alreadyTipped ? (
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            ) : (
              <HeartHandshake className="h-6 w-6 text-pink-600" />
            )}
          </div>
          <CardTitle>
            {search.status === 'success' || data.alreadyTipped
              ? 'Tip received'
              : search.status === 'cancel'
                ? 'Tip checkout canceled'
                : 'Leave a tip'}
          </CardTitle>
          <CardDescription>
            {search.status === 'success'
              ? 'Thank you. Your tip has been submitted and will appear on the invoice shortly.'
              : data.alreadyTipped && formattedTip
                ? `This invoice already has a tip of ${formattedTip}.`
                : search.status === 'cancel'
                  ? 'No payment was taken. You can retry whenever you are ready.'
                  : 'If your service went well, you can add a gratuity — payment is processed securely.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-3">
          {/* The tip is what this page is about, so it is the figure that gets
              the weight: the invoice total under "Tip received" reads as the
              tip a customer just left. */}
          <div className="mb-1 space-y-1 rounded-lg border border-border bg-muted/40 p-3 text-sm">
            <div className="flex items-center justify-between text-muted-foreground">
              <span>{data.invoiceNumber ? `Invoice ${data.invoiceNumber}` : 'Service'}</span>
              <span>{formatMoney(data.baseAmount)}</span>
            </div>
            {formattedTip ? (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Tip</span>
                <span className="font-semibold">{formattedTip}</span>
              </div>
            ) : null}
          </div>

          {search.status === 'cancel' && (
            <Button
              className="w-full"
              onClick={() =>
                void navigate({ to: '/tip-option/$id', params: { id }, replace: true })
              }
            >
              Choose tip amount
            </Button>
          )}

          <Button asChild variant="outline" className="w-full">
            <a href={returnHref}>Back to invoice</a>
          </Button>
        </CardContent>
      </Card>
    </PublicCenteredPage>
  );
}
